// assets/embroidery.js
class EmbroideryForm extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      form: '[data-bundle-form]',
      otherOption: '[js-em-other-option]',
      input: '[js-em-input]',
      color: '[js-em-color]',
      fontStyle: '[js-em-font-style]',
      validation: '.embroidery-validation-message',
      optionButton: '[js-option-button]',
      optionGroup: '[js-option-group]',
      optionButtonDefault: '[js-option-button-default]'
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.cart = document.querySelector('cart-drawer');
    this.form = this.querySelector(this.selectors.form);

    // Add-on product configuration from data-* passed by Liquid
    // data-addon-variant-id and data-addon-price should be set on <embroidery-form ...>
    const rawVariantId = this.dataset.addonVariantId;
    const parsedVariantId = rawVariantId ? parseInt(rawVariantId, 10) : null;
    this.addonVariantId = Number.isFinite(parsedVariantId) ? parsedVariantId : null;

    const rawPrice = this.dataset.addonPrice;
    const parsedPrice = rawPrice ? parseInt(rawPrice, 10) : 0;
    this.addonPrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;

    // Option/Variant maps for fallback resolution
    try {
      this.productOptions = JSON.parse(this.dataset.productOptions || '[]');
      this.productVariants = JSON.parse(this.dataset.productVariants || '[]');
    } catch (_e) {
      this.productOptions = [];
      this.productVariants = [];
    }

    this.optionButtons = this.querySelectorAll(this.selectors.optionButton);
    this.optionGroups = this.querySelectorAll(this.selectors.optionGroup);
    if (this.optionButtons.length > 0) {
      this.optionButtonDefault = this.querySelector(this.selectors.optionButtonDefault);
      this.optionButtonDefault.addEventListener('click', (e) => {
        this.handleOptionButtonDefaultClick(e);
      });
      this.optionButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          this.handleOptionButtonClick(e);
        });
      });
    }

    this.otherOptions = this.querySelectorAll(this.selectors.otherOption);
    if (this.otherOptions.length > 0) {
      this.otherOptions.forEach((option) => {
        option.addEventListener('change', (e) => {
          this.handleOtherOptionChange(e);
        });
      });
    }

    this.bindEvents();
  }

  bindEvents() {
    if (this.form) {
      // guard to ensure we only attach once
      if (!this._bound) {
        this._bound = true;
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      }
    }
  }

  handleOptionButtonDefaultClick(e) {
    e.preventDefault();
    this.optionButtonDefault.setAttribute('aria-selected', "true");
    this.optionButtons.forEach((button) => {
      button.setAttribute('aria-selected', "false");
    });
    this.optionGroups.forEach((group) => {
      group.classList.add('hidden');
      group.setAttribute('aria-hidden', "true");
      group.setAttribute('aria-expanded', "false");
    });
    this.querySelectorAll(this.selectors.otherOption).forEach((el) => { el.checked = false; });

    this.querySelector(this.selectors.input).required = true;
    this.querySelector(this.selectors.input).removeAttribute('disabled');

    this.querySelectorAll(this.selectors.color).forEach((color) => {
      color.removeAttribute('disabled');
    });

    this.querySelectorAll(this.selectors.fontStyle).forEach((fontStyle) => {
      fontStyle.removeAttribute('disabled');
    });
  }

  handleOptionButtonClick(e) {
    e.preventDefault();
    this.optionButtons.forEach((button) => {
      button.setAttribute('aria-selected', "false");
    });
    this.optionButtonDefault.setAttribute('aria-selected', "false");
    const button = e.target;
    const groupControls = button.getAttribute('aria-controls');
    this.optionGroups.forEach((group) => {
      if (group.id === groupControls) {
        group.classList.remove('hidden');
        group.setAttribute('aria-hidden', "false");
        group.setAttribute('aria-expanded', "true");
        button.setAttribute('aria-selected', "true");
      } else {
        if (!group.classList.contains('hidden')) {
          group.classList.add('hidden');
        }
        group.setAttribute('aria-hidden', "true");
        group.setAttribute('aria-expanded', "false");
      }
    });
  }

  handleOtherOptionChange(e) {
    if (e.target.dataset.initials === 'true') {
      this.querySelector(this.selectors.input).required = true;
      this.querySelector(this.selectors.input).removeAttribute('disabled');
    } else if (e.target.dataset.initials === 'false') {
      this.querySelector(this.selectors.input).removeAttribute('required');
      this.querySelector(this.selectors.input).disabled = true;
      this.querySelector(this.selectors.input).value = '';
    }

    if (e.target.dataset.color === 'true') {
      this.querySelectorAll(this.selectors.color).forEach((color) => {
        color.removeAttribute('disabled');
      });
    } else if (e.target.dataset.color === 'false') {
      this.querySelectorAll(this.selectors.color).forEach((color) => {
        color.disabled = true;
      });
      this.querySelectorAll(this.selectors.color).forEach((color) => {
        color.checked = false;
      });
    }

    this.querySelectorAll(this.selectors.fontStyle).forEach((fontStyle) => {
      fontStyle.disabled = true;
    });
  }

  // Resolve current PDP variant id robustly
  getSelectedVariantId() {
    // 1) Standard product form input[name="id"]
    const mainProductForm =
      document.querySelector('product-info form[action="/cart/add"]') ||
      document.querySelector('form[action="/cart/add"]');

    const idInput = mainProductForm?.querySelector('input[name="id"]');
    if (idInput?.value) {
      const v = parseInt(idInput.value, 10);
      if (Number.isFinite(v)) return v;
    }

    // 2) Fallback via selected options
    const values = this.productOptions.map((optName) => {
      const sel = mainProductForm?.querySelector(`select[name="options[${optName}]"]`);
      if (sel && sel.value) return sel.value;
      const radio = mainProductForm?.querySelector(`input[type="radio"][name="options[${optName}]"]:checked`);
      if (radio && radio.value) return radio.value;
      return null;
    });

    const match = this.productVariants.find((v) => {
      if (!Array.isArray(v.options) || v.options.length !== values.length) return false;
      for (let i = 0; i < values.length; i++) {
        if (values[i] == null) return false;
        if (String(v.options[i]) !== String(values[i])) return false;
      }
      return true;
    });

    if (match?.id) {
      const v = parseInt(match.id, 10);
      if (Number.isFinite(v)) return v;
    }

    return null;
  }

  // Normalize returned HTML so cart-drawer.js's selectors always succeed
  normalizeSectionsForDrawer(sectionsObj) {
    const out = { ...sectionsObj };

    if (typeof out['cart-drawer'] === 'string' && !out['cart-drawer'].includes('id="CartDrawer"')) {
      out['cart-drawer'] = `<div id="CartDrawer">${out['cart-drawer']}</div>`;
    }
    if (typeof out['cart-icon-bubble'] === 'string' && !out['cart-icon-bubble'].includes('shopify-section')) {
      out['cart-icon-bubble'] = `<div class="shopify-section">${out['cart-icon-bubble']}</div>`;
    }
    return out;
  }

  async refreshCartUI(lastAddedId) {
    try {
      // multi-section JSON if supported
      const res = await fetch(
        `${(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') }cart?sections=cart-drawer,cart-icon-bubble`,
        { headers: { Accept: 'application/json' } }
      );

      let sectionsObj;
      if (res.ok) {
        sectionsObj = await res.json();
      } else {
        // fallback to per-section HTML
        const base = (window.routes && window.routes.cart_url) || '/cart';
        const [drawerHTML, bubbleHTML] = await Promise.all([
          fetch(`${base}?section_id=cart-drawer`).then((r) => r.text()),
          fetch(`${base}?section_id=cart-icon-bubble`).then((r) => r.text())
        ]);
        sectionsObj = { 'cart-drawer': drawerHTML, 'cart-icon-bubble': bubbleHTML };
      }

      const normalized = this.normalizeSectionsForDrawer(sectionsObj);

      if (this.cart && typeof this.cart.renderContents === 'function') {
        this.cart.renderContents({ id: lastAddedId, sections: normalized });
      } else {
        // safe fallback refresh
        const parser = new DOMParser();

        const drawerTarget = document.querySelector('#CartDrawer');
        if (drawerTarget) {
          const frag = parser.parseFromString(normalized['cart-drawer'], 'text/html').querySelector('#CartDrawer');
          drawerTarget.innerHTML = frag ? frag.innerHTML : normalized['cart-drawer'];
        }

        const bubbleTarget = document.getElementById('cart-icon-bubble');
        if (bubbleTarget) {
          const frag = parser.parseFromString(normalized['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
          bubbleTarget.innerHTML = frag ? frag.innerHTML : normalized['cart-icon-bubble'];
        }
      }
    } catch (err) {
      console.error('[Embroidery] refreshCartUI error:', err);
    }
  }

  resetForm() {
    // Clear text inputs
    this.querySelectorAll(this.selectors.input).forEach((el) => {
      el.value = '';
    });
  
    // Uncheck all radios
    this.querySelectorAll(
      `${this.selectors.color}, ${this.selectors.fontStyle}, ${this.selectors.otherOption}`
    ).forEach((el) => {
      el.checked = false;
    });
  
    // Restore disabled/required state to the initial "nothing selected" state
    const initialsInput = this.querySelector(this.selectors.input);
    if (initialsInput) {
      initialsInput.required = true;
      initialsInput.disabled = false;
    }
  
    this.querySelectorAll(this.selectors.color).forEach((el) => { el.disabled = false; });
    this.querySelectorAll(this.selectors.fontStyle).forEach((el) => { el.disabled = false; });
    this.querySelectorAll(this.selectors.otherOption).forEach((el) => { el.checked = false; });
  
    const validation = this.querySelector(this.selectors.validation);
    if (validation) validation.textContent = '';
  }

  handleSubmit(e) {
    e.preventDefault();

    const validation = this.querySelector(this.selectors.validation);

    const mainVariantId = this.getSelectedVariantId();
    if (!mainVariantId) {
      if (validation) validation.textContent = 'Please select a size/variant before adding to cart.';
      return;
    }

    const initials = this.querySelector(this.selectors.input)?.value?.trim();
    if (!initials && this.querySelector(this.selectors.input).required) {
      if (validation) validation.textContent = 'Please enter your initials.';
      return;
    }
    if (validation) validation.textContent = '';

    let colorValue, fontStyleValue, otherOptionValue;
    this.querySelectorAll(this.selectors.color).forEach((i) => { if (i.checked) colorValue = i.value; });
    this.querySelectorAll(this.selectors.fontStyle).forEach((i) => { if (i.checked) fontStyleValue = i.value; });
    this.querySelectorAll(this.selectors.otherOption).forEach((i) => { if (i.checked) otherOptionValue = i.dataset.property; });

    const groupId = `emb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    const sharedProps = {
      'Initials': initials,
      'Embroidery': 'Yes',
      '_embroidery_group': groupId,
      '_uniqueValue': this.dataset.embroideryId || ''
    };
    if (colorValue) sharedProps['Thread Color'] = colorValue;
    if (fontStyleValue) sharedProps['Font Style'] = fontStyleValue;
    if (otherOptionValue) sharedProps['Collection'] = otherOptionValue;

    const items = [
      { id: mainVariantId, quantity: 1, properties: { ...sharedProps } }
    ];

    if (this.addonVariantId) {
      items.push({
        id: this.addonVariantId,
        quantity: 1,
        properties: {
          ...sharedProps,
          '_is_embroidery_fee': 'true',
          '_main_product_variant': mainVariantId
        }
      });
    }

    // IMPORTANT: include Accept: application/json so Shopify returns sections if available
    fetch(`${(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') }cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
      .then(async (res) => {
        const text = await res.text();
        if (!res.ok) throw new Error(text || `Add to cart failed (${res.status})`);
        let parsed = {};
        try { parsed = JSON.parse(text); } catch (_e) {}
        return parsed;
      })
      .then((parsed) => {
        // If the theme returned sections, forward to the drawer renderer
        if (parsed && parsed.sections && this.cart && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(parsed);
          this.resetForm();
        } else {
          // Otherwise, force-refresh the cart UI and open the drawer
          this.refreshCartUI(mainVariantId).then(() => {
            if (this.cart && typeof this.cart.open === 'function') this.cart.open();
            this.resetForm();
          });
        }
      })
      .catch((err) => {
        console.error('Embroidery add error:', err);
        if (validation) validation.textContent = 'Sorry—couldn’t add to cart. Please try again.';
      });
  }
}

customElements.define('embroidery-form', EmbroideryForm);
